import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plug, RefreshCw, CheckCircle2, XCircle, Unlink, ExternalLink } from "lucide-react";
import { API, authHeaders } from "./shared";

const inputCls = "w-full bg-brand-bg border border-brand-border text-brand-text font-sans text-sm px-3 py-2 focus:border-brand-gold outline-none";

const SupplierConnectPanel = ({ supplier, onClose, onChanged }) => {
  const [form, setForm] = useState({ store_url: supplier.store_url || "", consumer_key: "", consumer_secret: "" });
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [products, setProducts] = useState([]);

  const loadProducts = useCallback(() => {
    axios.get(`${API}/admin/suppliers/${supplier.id}/products`, authHeaders())
      .then((r) => setProducts(r.data))
      .catch(() => {});
  }, [supplier.id]);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  const saveConnection = async () => {
    if (!form.store_url || !form.consumer_key || !form.consumer_secret) {
      toast.error("Store URL, Consumer Key and Consumer Secret are all required"); return;
    }
    setBusy("save");
    try {
      await axios.put(`${API}/admin/suppliers/${supplier.id}/connection`, form, authHeaders());
      toast.success("Connection saved securely");
      setForm((f) => ({ ...f, consumer_key: "", consumer_secret: "" }));
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save connection");
    } finally { setBusy(""); }
  };

  const testConnection = async () => {
    setBusy("test"); setTestResult(null);
    try {
      const r = await axios.post(`${API}/admin/suppliers/${supplier.id}/test-connection`, {}, authHeaders());
      setTestResult(r.data);
      r.data.connection_status === "connected" ? toast.success("Connection OK") : toast.error("Connection failed");
      onChanged();
    } catch (e) {
      const msg = e.response?.data?.detail || "Test failed";
      setTestResult({ connection_status: "error", message: msg });
      toast.error(msg);
    } finally { setBusy(""); }
  };

  const syncProducts = async () => {
    setBusy("sync");
    try {
      const r = await axios.post(`${API}/admin/suppliers/${supplier.id}/sync`, {}, authHeaders());
      toast.success(`Synced ${r.data.synced} product${r.data.synced === 1 ? "" : "s"}`);
      loadProducts();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setBusy(""); }
  };

  const disconnect = async () => {
    if (!window.confirm("Remove the saved WooCommerce keys for this supplier?")) return;
    try {
      await axios.delete(`${API}/admin/suppliers/${supplier.id}/connection`, authHeaders());
      toast.success("Disconnected");
      onChanged();
    } catch { toast.error("Failed to disconnect"); }
  };

  const toggleApprove = async (p) => {
    try {
      const r = await axios.patch(`${API}/admin/suppliers/${supplier.id}/products/${p.id}/approve`, {}, authHeaders());
      setProducts((list) => list.map((x) => (x.id === p.id ? { ...x, approved: r.data.approved } : x)));
    } catch { toast.error("Failed to update approval"); }
  };

  return (
    <div className="border border-brand-gold/50 bg-brand-surface p-5 mb-6" data-testid="supplier-connect-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-lg text-brand-text flex items-center gap-2">
          <Plug size={16} className="text-brand-gold" /> {supplier.name} — WooCommerce Connection
        </h3>
        <button data-testid="connect-panel-close" onClick={onClose} className="p-1.5 text-brand-text-muted hover:text-brand-text"><X size={16} /></button>
      </div>
      <p className="font-sans text-xs text-brand-text-muted mb-4">
        Read-only bridge: products are pulled INTO the admin dashboard only. Nothing is published to your website, no orders are placed, and keys are stored server-side (never shown again).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="font-sans text-xs text-brand-text-muted">Store URL (https://…)</label>
          <input data-testid="woo-store-url-input" className={inputCls} value={form.store_url} onChange={(e) => setForm((f) => ({ ...f, store_url: e.target.value }))} placeholder="https://yourstore.com" />
        </div>
        <div>
          <label className="font-sans text-xs text-brand-text-muted">Consumer Key (ck_…)</label>
          <input data-testid="woo-consumer-key-input" type="password" className={inputCls} value={form.consumer_key} onChange={(e) => setForm((f) => ({ ...f, consumer_key: e.target.value }))} placeholder={supplier.credentials_set ? "•••••••• (saved)" : "ck_xxxxxxxx"} />
        </div>
        <div>
          <label className="font-sans text-xs text-brand-text-muted">Consumer Secret (cs_…)</label>
          <input data-testid="woo-consumer-secret-input" type="password" className={inputCls} value={form.consumer_secret} onChange={(e) => setForm((f) => ({ ...f, consumer_secret: e.target.value }))} placeholder={supplier.credentials_set ? "•••••••• (saved)" : "cs_xxxxxxxx"} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <button data-testid="woo-save-connection-btn" onClick={saveConnection} disabled={busy !== ""} className="px-4 py-2 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors disabled:opacity-50">
          {busy === "save" ? "Saving…" : "Save Connection"}
        </button>
        <button data-testid="woo-test-connection-btn" onClick={testConnection} disabled={busy !== "" || !supplier.credentials_set} className="flex items-center gap-2 px-4 py-2 border border-brand-gold text-brand-gold font-sans text-xs hover:bg-brand-gold/10 transition-colors disabled:opacity-50">
          <CheckCircle2 size={14} /> {busy === "test" ? "Testing…" : "Test Connection"}
        </button>
        <button data-testid="woo-sync-btn" onClick={syncProducts} disabled={busy !== "" || !supplier.credentials_set} className="flex items-center gap-2 px-4 py-2 border border-brand-border text-brand-text font-sans text-xs hover:border-brand-gold transition-colors disabled:opacity-50">
          <RefreshCw size={14} className={busy === "sync" ? "animate-spin" : ""} /> {busy === "sync" ? "Syncing…" : "Sync Products"}
        </button>
        {supplier.credentials_set && (
          <button data-testid="woo-disconnect-btn" onClick={disconnect} disabled={busy !== ""} className="flex items-center gap-2 px-4 py-2 border border-red-500/40 text-red-400 font-sans text-xs hover:bg-red-500/10 transition-colors disabled:opacity-50">
            <Unlink size={14} /> Disconnect
          </button>
        )}
      </div>

      {testResult && (
        <div data-testid="woo-test-result" className={`flex items-start gap-2 font-sans text-xs p-3 border mb-4 ${testResult.connection_status === "connected" ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-red-500/40 text-red-400 bg-red-500/10"}`}>
          {testResult.connection_status === "connected" ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <XCircle size={14} className="mt-0.5 shrink-0" />}
          {testResult.message}
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <h4 className="font-sans text-xs uppercase tracking-wider text-brand-text-muted">Synced Products ({products.length}) — admin-only until approved</h4>
      </div>
      {products.length === 0 ? (
        <p data-testid="woo-no-products" className="font-sans text-xs text-brand-text-muted py-4">No products synced yet. Save the connection, test it, then click Sync Products.</p>
      ) : (
        <div className="overflow-x-auto border border-brand-border max-h-96 overflow-y-auto">
          <table className="w-full text-left" data-testid="supplier-products-table">
            <thead className="sticky top-0 bg-brand-surface">
              <tr className="border-b border-brand-border">
                {["", "Product", "SKU", "Price", "Stock", "Approved", ""].map((h, i) => (
                  <th key={i} className="font-sans text-[10px] uppercase tracking-wider text-brand-text-muted px-3 py-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} data-testid={`supplier-product-row-${p.id}`} className="border-b border-brand-border/40">
                  <td className="px-3 py-2">{p.image_url && <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover" />}</td>
                  <td className="px-3 py-2 font-sans text-sm text-brand-text max-w-[240px] truncate" title={p.name}>{p.name}</td>
                  <td className="px-3 py-2 font-sans text-xs text-brand-text-muted">{p.sku || "—"}</td>
                  <td className="px-3 py-2 font-sans text-sm text-brand-gold">{p.price ? `$${p.price}` : "—"}</td>
                  <td className={`px-3 py-2 font-sans text-xs ${p.stock_status === "instock" ? "text-green-400" : "text-red-400"}`}>{p.stock_status || "—"}</td>
                  <td className="px-3 py-2">
                    <button data-testid={`approve-toggle-${p.id}`} onClick={() => toggleApprove(p)} className={`font-sans text-[10px] uppercase px-2 py-1 border transition-colors ${p.approved ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-brand-border text-brand-text-muted hover:border-brand-gold"}`}>
                      {p.approved ? "Approved" : "Not approved"}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    {p.permalink && <a href={p.permalink} target="_blank" rel="noreferrer" className="text-brand-text-muted hover:text-brand-gold"><ExternalLink size={13} /></a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SupplierConnectPanel;
